import { useState } from 'react';
import { Select, TextInput, Menu, Button, Text, Group, Loader } from '@mantine/core';
import { IconBuilding, IconSearch, IconChevronDown } from '@tabler/icons-react';
import { useCompany } from '../../hooks/useCompany';
import { useAuth } from '../../hooks/useAuth';
import { Company } from '../../providers/CompanyProvider';

export function CompanySelector() {
  const { user } = useAuth();
  const {
    currentCompany,
    companies,
    setCurrentCompany,
    searchCompanies,
    loading,
  } = useCompany();

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Company[]>([]);
  const [searching, setSearching] = useState(false);

  const handleSearch = async (query: string) => {
    setSearchQuery(query);
    if (query.length >= 2) {
      setSearching(true);
      const results = await searchCompanies(query);
      setSearchResults(results);
      setSearching(false);
    } else {
      setSearchResults([]);
    }
  };

  if (loading) {
    return <Loader size="sm" />;
  }

  if (!currentCompany) {
    return null;
  }

  // Superadmin mode - search all companies
  if (user?.isSuperadmin) {
    return (
      <Menu shadow="md" width={300} position="bottom-end">
        <Menu.Target>
          <Button
            variant="subtle"
            rightSection={<IconChevronDown size={14} />}
            leftSection={<IconBuilding size={16} />}
          >
            {currentCompany.name}
          </Button>
        </Menu.Target>

        <Menu.Dropdown>
          <TextInput
            placeholder="Поиск компании..."
            leftSection={<IconSearch size={14} />}
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            mb="xs"
          />

          {searching && <Loader size="sm" mx="auto" display="block" />}

          {searchResults.map((company) => (
            <Menu.Item
              key={company.id}
              onClick={() => {
                setCurrentCompany(company);
                setSearchQuery('');
                setSearchResults([]);
              }}
            >
              {company.name}
            </Menu.Item>
          ))}

          {searchQuery.length >= 2 && searchResults.length === 0 && !searching && (
            <Text size="sm" c="dimmed" ta="center" py="xs">
              Компании не найдены
            </Text>
          )}
        </Menu.Dropdown>
      </Menu>
    );
  }

  // Regular user mode - dropdown of own companies
  if (companies.length <= 1) {
    return (
      <Group gap="xs">
        <IconBuilding size={16} />
        <Text size="sm">{currentCompany.name}</Text>
      </Group>
    );
  }

  return (
    <Select
      value={currentCompany.id}
      data={companies.map((c) => ({ value: c.id, label: c.name }))}
      onChange={(value) => {
        const company = companies.find((c) => c.id === value);
        if (company) setCurrentCompany(company);
      }}
      leftSection={<IconBuilding size={16} />}
      w={200}
    />
  );
}
